import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Code2, BookOpenCheck, FunctionSquare, SearchCheck } from "lucide-react";
import { usePublicLang } from "../lib/publicLang";
import MethodologyEn from "./MethodologyEn";

/**
 * Single-page technical methodology — the "share on r/algotrading" artifact.
 * Dense, formula-heavy, defensible, no marketing fluff.
 */

const CITATIONS = [
  { key: "bailey_lopez_prado_2014", label: "Bailey & López de Prado (2014)", ref: "The Deflated Sharpe Ratio: Correcting for Selection Bias, Backtest Overfitting and Non-Normality — Journal of Portfolio Management, 40(5)" },
  { key: "lopez_prado_2018", label: "López de Prado (2018)", ref: "Advances in Financial Machine Learning — Wiley" },
  { key: "cont_kukanov_stoikov_2013", label: "Cont, Kukanov & Stoikov (2013)", ref: "The Price Impact of Order Book Events — Journal of Financial Econometrics" },
  { key: "harvey_2017", label: "Harvey (2017)", ref: "Presidential Address: The Scientific Outlook in Financial Economics — Journal of Finance" },
];

export default function Methodology() {
  const [lang, setLang] = usePublicLang();
  const [verifCount, setVerifCount] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reporting/verification-count`)
      .then((r) => r.json())
      .then((d) => setVerifCount(d.count))
      .catch(() => {});
  }, []);
  if (lang === "en") return <MethodologyEn lang={lang} setLang={setLang} />;
  return (
    <div className="min-h-screen bg-bg text-white" data-testid="methodology-page">
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon.svg" alt="ANATRADEX" className="w-9 h-9 rounded-lg" width="36" height="36" />
            <div>
              <div className="font-heading font-bold text-lg leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">Methodology</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/audit" className="text-sm text-white/60 hover:text-white flex items-center gap-1">
              /audit
            </Link>
            <Link to="/" className="text-sm text-white/60 hover:text-white flex items-center gap-1.5">
              <ArrowLeft size={14} /> Retour
            </Link>
          </div>
        </div>
      </nav>

      <article className="max-w-4xl mx-auto px-6 py-16">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent-blue/10 border border-accent-blue/30 rounded-full text-xs uppercase tracking-widest text-accent-blue mb-4">
          <BookOpenCheck size={12} /> Open methodology · Vérifiable · Reproductible
        </div>
        <h1 className="font-heading font-bold text-4xl md:text-5xl leading-tight tracking-tight mb-4">
          ANATRADEX — Methodology
        </h1>
        <p className="text-white/60 text-lg mb-2 leading-relaxed">
          Comment nous mesurons un edge <em>vraiment</em> significatif : PBO, Deflated Sharpe,
          Purged K-Fold, sign-flip permutation, order flow impact. Formules complètes, code source des tests,
          hash-chain d&apos;audit vérifiable dans votre navigateur.
        </p>
        <div className="text-xs text-white/40 mb-12">Dernière révision : Février 2026 · Version 1.0 · <a href="/audit" className="text-accent-blue underline">Vérifier l&apos;intégrité</a></div>

        <div className="prose-content space-y-12 text-white/80 leading-relaxed">

          <section>
            <h2 className="font-heading text-2xl mb-3 flex items-center gap-2"><FunctionSquare size={20} /> 1. Le problème que nous adressons</h2>
            <p>Les backtests publiés par les vendeurs de stratégies retail affichent couramment des <b className="text-white">Sharpe ratios supérieurs à 3</b>. En finance quantitative institutionnelle, un Sharpe {">"} 2 net de frais est déjà un <b>outlier</b> — et Harvey (2017, Presidential Address, Journal of Finance) estime que la majorité des résultats publiés en économie financière sont probablement des faux positifs, faute de correction pour le multiple testing. Comment expliquer cet écart ?</p>
            <p className="mt-3">Trois biais tuent la validité de 90 % des backtests amateurs :</p>
            <ol className="space-y-2 mt-3">
              <li><b className="text-white">Data snooping / multiple testing bias</b> — on teste 100 stratégies, on garde la meilleure. Statistiquement, obtenir un Sharpe {">"} 3 par pur hasard sur 100 essais aléatoires est banal.</li>
              <li><b className="text-white">Look-ahead bias</b> — la stratégie « voit le futur » via une fuite de features (ex. utiliser le high de la journée pour trader au milieu de la journée).</li>
              <li><b className="text-white">Survivorship bias</b> — backtest uniquement sur les actifs qui existent aujourd&apos;hui.</li>
            </ol>
            <p className="mt-3">ANATRADEX applique trois contre-mesures statistiques éprouvées :</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-3">2. Probability of Backtest Overfitting (PBO)</h2>
            <p><b>Objectif</b> : mesurer la probabilité qu&apos;une stratégie sélectionnée « in-sample » soit surajustée.</p>
            <p><b>Référence</b> : Bailey, Borwein, López de Prado & Zhu (2016), <em>The Probability of Backtest Overfitting</em>, Journal of Computational Finance.</p>
            <div className="rounded-lg border border-white/10 bg-black/40 p-4 my-4 font-mono text-sm">
              <div className="text-white/50 text-xs mb-2">// Pseudo-code</div>
              <pre className="text-white/80 text-xs overflow-x-auto"><code>{`def pbo(returns_matrix, n_splits=8):
    """
    returns_matrix: N strategies × T observations
    Retourne P(rank_IS_best_strat_OOS <= median).
    Un PBO < 30% = edge probablement réel.
    """
    N, T = returns_matrix.shape
    fold_size = T // n_splits
    n_overfit = 0
    for combo in combinations(range(n_splits), n_splits // 2):
        train_folds = list(combo)
        test_folds = [i for i in range(n_splits) if i not in train_folds]
        # Sharpe in-sample par stratégie
        is_sharpes = [sharpe(returns_matrix[s, train_folds]) for s in range(N)]
        best_strat_is = argmax(is_sharpes)
        # Rank OOS
        oos_sharpes = [sharpe(returns_matrix[s, test_folds]) for s in range(N)]
        rank_oos = rankdata(oos_sharpes)[best_strat_is]
        if rank_oos <= N // 2:  # sous la médiane OOS
            n_overfit += 1
    return n_overfit / C(n_splits, n_splits//2)`}</code></pre>
            </div>
            <p><b>Zones d&apos;interprétation ANATRADEX</b> (seuils indicatifs, jamais un verdict automatique) :</p>
            <ul className="space-y-1 mt-2">
              <li>PBO {"<"} 15 % → zone <b className="text-up">edge très probable</b></li>
              <li>15 % ≤ PBO {"<"} 30 % → edge à surveiller</li>
              <li>PBO ≥ 30 % → surajustement probable, <b className="text-warn">poubelle</b></li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-3">3. Deflated Sharpe Ratio (DSR)</h2>
            <p><b>Objectif</b> : ajuster le Sharpe pour le nombre d&apos;essais (multiple testing) et la non-normalité (skewness, kurtosis).</p>
            <p><b>Référence</b> : Bailey & López de Prado (2014).</p>
            <div className="rounded-lg border border-white/10 bg-black/40 p-4 my-4 font-mono text-sm">
              <div className="text-white/50 text-xs mb-2">// Formule</div>
              <pre className="text-white/80 text-xs overflow-x-auto"><code>{`SR_0 = expected_max_sharpe_under_H0(N_trials, T_obs)
     ≈ sqrt(V[sharpes]) × ((1 - γ) × Φ⁻¹(1 - 1/N)
                          + γ × Φ⁻¹(1 - 1/(N·e)))

Z_score = (SR_observed - SR_0) × sqrt(T - 1) /
          sqrt(1 - skew·SR + ((kurt-1)/4)·SR²)

DSR_probability = Φ(Z_score)   # normal CDF

# DSR > 95% → edge probablement réel, malgré N essais.`}</code></pre>
            </div>
            <p>Où <em>γ</em> ≈ 0,5772 (Euler-Mascheroni), <em>Φ</em> = CDF normale, <em>skew</em> et <em>kurt</em> = moments empiriques des rendements.</p>
            <p className="mt-3">C&apos;est <b>la seule métrique qui résiste à l&apos;examen d&apos;une prop firm ou d&apos;un investisseur sérieux</b>. Un Sharpe brut de 4 avec DSR de 40 % dit : *« vous avez été chanceux, il y a 60 % de chances que ce ne soit que du bruit »*.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-3">4. Purged K-Fold Cross-Validation</h2>
            <p><b>Problème</b> : les rendements financiers présentent une <b>autocorrélation</b> qui viole l&apos;hypothèse d&apos;indépendance de la K-Fold classique.</p>
            <p><b>Solution López de Prado (2018)</b> : on introduit une <b>zone de purge</b> entre le train et le test pour éliminer le chevauchement d&apos;information.</p>
            <div className="rounded-lg border border-white/10 bg-black/40 p-4 my-4 font-mono text-xs overflow-x-auto">
              <pre className="text-white/80">{`Timeline : [====TRAIN====][purge][====TEST====][embargo]

purge_length ≈ max_holding_period of signals
embargo    ≈ 1% of T (protège contre serial correlation leak)`}</pre>
            </div>
            <p>Résultat : les métriques out-of-sample sont statistiquement <b>indépendantes</b> des données d&apos;entraînement. Aucune fuite temporelle.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-3">5. Sign-flip permutation test</h2>
            <p><b>Objectif</b> : tester H₀ « la stratégie n&apos;a aucun edge » (rendements alignés par hasard).</p>
            <div className="rounded-lg border border-white/10 bg-black/40 p-4 my-4 font-mono text-sm">
              <pre className="text-white/80 text-xs overflow-x-auto"><code>{`observed_sharpe = sharpe(trade_returns)
n_iters = 2000   # seed déterministe 42817
count = 0
for _ in range(n_iters):
    # Flip aléatoirement le signe de chaque return sous H0
    shuffled = trade_returns * random_choice([-1, +1], size=T)
    if sharpe(shuffled) >= observed_sharpe:
        count += 1
p_value = count / n_iters

# p < 0.05 → edge statistiquement significatif à 5%
# p < 0.01 → edge très solide`}</code></pre>
            </div>
            <p>C&apos;est un test <b>non paramétrique</b> — pas d&apos;hypothèse sur la distribution des rendements. Robuste aux fat tails, à la skewness, aux régimes.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-3">6. Order Flow Impact (OFI)</h2>
            <p><b>Modèle</b> : Cont, Kukanov & Stoikov (2013).</p>
            <div className="rounded-lg border border-white/10 bg-black/40 p-4 my-4 font-mono text-xs overflow-x-auto">
              <pre className="text-white/80">{`Pour chaque événement k du carnet (bid ou ask), on calcule :

e_k = { +q_k      si événement à l'achat au meilleur bid
      { -q_k      si événement à la vente au meilleur ask
      { -Δq_k     si annulation
      { ...

OFI(t) = Σ e_k pour t-Δt ≤ k ≤ t

# OFI positif → pression acheteuse cumulée
# Corrélation prédictive avec Δ_price à horizon 10-60s`}</pre>
            </div>
            <p>ANATRADEX calcule l&apos;OFI en temps réel via Databento MBP-10 ou Polygon NBBO (fallback synthetic Cont-Kukanov-Stoikov). Chaque signal l&apos;affiche comme feature ML.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-3">7. Kelly fractionnaire multi-actifs</h2>
            <p><b>Formule scalaire</b> (2 outcomes) : f* = (p·b − (1−p)) / b</p>
            <p><b>Formule multi-actifs</b> (avec matrice de covariance Σ et vecteur de rendements attendus μ) :</p>
            <div className="rounded-lg border border-white/10 bg-black/40 p-4 my-4 font-mono text-sm overflow-x-auto">
              <pre className="text-white/80 overflow-x-auto">{`f_optimal = Σ⁻¹ · μ

# En pratique ANATRADEX :
f_deployed = f_optimal / 5    # Fractional Kelly, pratique standard en gestion quantitative
             puis clipé à ≤ 2% par actif (comptes à drawdown contraint)`}</pre>
            </div>
            <p>Le Full Kelly théorique suppose des rendements gaussiens iid — hypothèse fausse en pratique. Kelly/5 survit aux fat tails, aux ruptures de régime et à l&apos;estimation bruitée de μ.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-3">8. Hash-chain d&apos;audit SHA-256</h2>
            <p>Chaque événement (signal, ordre, config change) est immuablement scellé :</p>
            <div className="rounded-lg border border-white/10 bg-black/40 p-4 my-4 font-mono text-sm">
              <pre className="text-white/80 text-xs overflow-x-auto"><code>{`canonical = json.dumps({
    "prev_hash": prev_hash,
    "event_type": event_type,
    "payload": payload,
    "timestamp": timestamp,
}, sort_keys=True, separators=(",", ":"))

event_hash = hashlib.sha256(canonical.encode()).hexdigest()`}</code></pre>
            </div>
            <p>Le lien chaîné (<code>prev_hash</code>) implique que toute altération rétroactive brise TOUS les hash suivants. <Link to="/audit" className="text-accent-blue underline">Vérifiez la chaîne dans votre navigateur</Link> — nous exposons les <code>canonical_bytes</code> exacts, votre `crypto.subtle.digest` les re-hashe côte serveur-side. Zero-trust.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-3">9. Ce que nous ne prétendons PAS être</h2>
            <ul className="space-y-2">
              <li>❌ Un HFT à latence sub-milliseconde (nous sommes en Python/FastAPI — latence p50/p95/p99 <b>mesurée en continu</b> et publiée en temps réel via <code>/api/reporting/live-stats</code>, plutôt qu&apos;un chiffre marketing figé)</li>
              <li>❌ Un data provider — nous consommons Databento/Polygon/Twelve Data</li>
              <li>❌ Un régulé MiFID Investment Firm (les exports de type réglementaire sont purement démonstratifs et ne sont pas soumissibles à l&apos;ESMA)</li>
              <li>❌ Une garantie de performance future</li>
              <li>❌ Un remplaçant d&apos;un conseiller financier régulé</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-3">10. Formules exactes des métriques affichées</h2>
            <p className="mb-4">Chaque métrique visible sur la plateforme (badge landing, rapport PDF, backtests) est
            calculée avec la formule ci-dessous, <b>sans coefficient d&apos;ajustement, sans borne artificielle,
            sans verdict pré-décidé</b>. Les jeux de données d&apos;exemple sont explicitement étiquetés
            «&nbsp;démonstration&nbsp;» — jamais présentés comme des performances réelles.</p>
            <div className="overflow-x-auto" data-testid="formulas-table">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left border-b border-white/20">
                    <th className="py-2 pr-4 text-white">Métrique</th>
                    <th className="py-2 pr-4 text-white">Formule exacte</th>
                    <th className="py-2 text-white">Ajustement post-calcul</th>
                  </tr>
                </thead>
                <tbody className="text-white/70">
                  {[
                    ["Sharpe Ratio", "mean(r) / std(r, ddof=1) × √250", "Aucun"],
                    ["Profit Factor", "Σ(gains) / Σ(|pertes|)", "Aucun"],
                    ["Max Drawdown", "max(pic − creux) / pic sur equity composée Π(1+rᵢ)", "Aucun"],
                    ["Win Rate", "n(trades gagnants) / n(trades)", "Aucun"],
                    ["Permutation p-value", "Sign-flip : P(Sharpe_H0 ≥ Sharpe_obs) sur 2 000 itérations (seed déterministe 42817, identique démo et données réelles), signes inversés aléatoirement sous H0 (pas d'edge)", "Aucun"],
                    ["PBO", "Bailey-Borwein-López-Zhu 2015 : P(rang OOS > médiane) via C(S, S/2) combinaisons, S=8 splits", "Aucun"],
                    ["Deflated Sharpe (DSR)", "Bailey-López de Prado 2014 : Φ((SR − SR₀)/√V[SR]), SR₀ = √V{SR_essais} × E[max Z sous H0], variance Mertens 2002 (skew + kurtosis)", "Aucun"],
                  ].map(([m, f, adj]) => (
                    <tr key={m} className="border-b border-white/5 align-top">
                      <td className="py-2.5 pr-4 font-semibold text-white whitespace-nowrap">{m}</td>
                      <td className="py-2.5 pr-4 font-mono text-xs leading-relaxed">{f}</td>
                      <td className="py-2.5 text-up text-xs">{adj}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm text-white/60">Pourquoi le sign-flip et pas un shuffle ? Mélanger l&apos;ordre
            des rendements préserve la moyenne et l&apos;écart-type — le Sharpe permuté serait identique à l&apos;observé
            et le test serait dégénéré (p=1.0 systématique). L&apos;inversion aléatoire des signes détruit l&apos;edge
            sous H0 tout en conservant la distribution des amplitudes.</p>
          </section>

          <section>
            <h2 className="font-heading text-2xl mb-3">11. Références bibliographiques</h2>
            <ul className="space-y-2 text-sm">
              {CITATIONS.map((c) => (
                <li key={c.key}>
                  <b className="text-white">{c.label}</b> — <span className="text-white/60">{c.ref}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-2xl border border-accent-blue/30 bg-accent-blue/5 p-6 mt-12" data-testid="reproducibility-section">
            <h3 className="font-heading text-xl mb-3 flex items-center gap-2"><Code2 size={18} /> Reproductibilité — vérifiez nos calculs vous-même</h3>
            <p>Chaque rapport PDF affiche son <b>seed déterministe (42817)</b> et le nombre d&apos;itérations
            de permutation. Les données brutes derrière chaque chiffre sont exportables :</p>
            <ul className="mt-3 space-y-1.5 text-sm font-mono">
              <li><span className="text-accent-blue">GET /api/reporting/dd-pack-data</span> — JSON : rendements par trade, distribution nulle complète de permutation, entrées PBO/DSR, seed</li>
              <li><span className="text-accent-blue">GET /api/reporting/dd-pack-data?format=csv</span> — CSV des rendements par trade</li>
            </ul>
            <p className="mt-4 text-sm">Reproduction de la p-value de permutation en ~15 lignes de Python :</p>
            <div className="rounded-lg border border-white/10 bg-black/40 p-4 my-3 font-mono text-xs overflow-x-auto">
              <pre className="text-white/80 overflow-x-auto"><code>{`import json, random, urllib.request
BASE = "${typeof window !== "undefined" ? window.location.origin : "https://votre-instance"}"
data = json.load(urllib.request.urlopen(
    BASE + "/api/reporting/dd-pack-data?use_real_trades=false"))
sym = data["per_symbol"][0]          # ex. EURUSD
r, n = sym["returns"], len(sym["returns"])

def sharpe(x):
    m = sum(x) / len(x)
    sd = (sum((v - m) ** 2 for v in x) / (len(x) - 1)) ** 0.5
    return m / sd * 250 ** 0.5 if sd else 0

rng = random.Random(sym["perm_seed"])   # 42817
count = 0
for _ in range(sym["perm_n_iters"]):
    flipped = [v * (1 if rng.random() > 0.5 else -1) for v in r]
    if sharpe(flipped) >= sharpe(r):
        count += 1
p = count / sym["perm_n_iters"]
assert round(p, 4) == sym["permutation_p"]  # identique au rapport`}</code></pre>
            </div>
            <p className="text-sm">La <Link to="/audit" className="text-accent-blue underline">page /audit</Link> permet
            de vérifier l&apos;intégrité de la chaîne d&apos;événements (le hash du dernier bloc est imprimé
            dans chaque PDF). Le rapport est régénérable à tout moment — mêmes entrées, mêmes sorties.</p>
            {verifCount > 0 && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-up/30 bg-up/5 px-3.5 py-1.5 text-xs text-up" data-testid="verification-count-badge">
                <SearchCheck size={13} />
                {verifCount.toLocaleString("fr-FR")} export{verifCount > 1 ? "s" : ""} de données brutes effectué{verifCount > 1 ? "s" : ""} — chaque export est une vérification indépendante possible
              </div>
            )}
          </section>

        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap gap-3 justify-between items-center">
          <div className="text-xs text-white/40">
            Contact technique : <a href="mailto:tech@anatradex.com" className="text-accent-blue">tech@anatradex.com</a>
          </div>
          <div className="flex gap-3">
            <Link to="/audit" className="btn text-sm px-5 py-2.5" data-testid="methodology-audit-cta">
              Vérifier la chaîne d&apos;audit
            </Link>
            <Link to="/beta" className="btn btn-primary text-sm px-5 py-2.5" data-testid="methodology-beta-cta">
              Rejoindre la beta <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        <p className="mt-8 text-[11px] text-white/35 leading-relaxed" data-testid="methodology-risk-disclaimer">
          ⚠️ ANATRADEX est un outil d&apos;analyse statistique, pas un conseil en investissement. ANATRADEX n&apos;est pas
          enregistré comme Conseiller en Investissements Financiers (CIF) auprès de l&apos;AMF/ORIAS. Le trading comporte
          un risque de perte pouvant atteindre 100 % du capital — 70 à 85 % des traders particuliers perdent de
          l&apos;argent (ESMA). Les performances passées ne préjugent pas des performances futures.
        </p>
      </article>
    </div>
  );
}
